import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Box, ButtonBase, Chip, Icon } from '@mui/material';
import { withRouter } from 'utils';   // or react-router’s withRouter v5 helper
import { isTabbedPage } from 'utils/pathUtils';
import MicIcon from '@mui/icons-material/Mic';
import MicOutlinedIcon from '@mui/icons-material/MicOutlined';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';

import { ChatBotContext } from 'containers/ChatBotContext';
import AudioConversation from 'components/chatbot/AudioConversation';

const SELECTED_BG = 'rgb(133, 165, 168)';           // brand teal for selected
const ICON_ON     = '#FFFFFF';
const ICON_OFF    = 'rgba(255,255,255,0.70)';

class SupportIcon extends Component {
static contextType = ChatBotContext;

state = {
micOn   : false,
busy    : false,
showTray: false,
hovering: false,      // desktop hover only
};

lastNoneSelectedClosed = false;
isTouch = false;
wrapperRef = null;

componentDidMount() {
// Touch / no-hover detection
this.isTouch =
typeof window !== 'undefined' &&
(window.matchMedia('(hover: none)').matches ||
window.matchMedia('(pointer: coarse)').matches);

// initial mic state
const audio = AudioConversation.getInstance();
this.setState({ micOn: audio.isConversationStarted() });

// audio state sync
this.audioStateHandler = (e) => {
  const s = e.detail || {};
  this.setState({ micOn: !!s.active });
};
window.addEventListener('audio-state', this.audioStateHandler);

// chat closed elsewhere
this.chatClosedHandler = () => {
  try { this.context.setClicked?.(false); } catch {}
  // hide tray if not hovering and mic off
  this.setState((prev) => ({ showTray: prev.hovering ? true : !!prev.micOn }));
};
window.addEventListener('chat-closed', this.chatClosedHandler);
window.addEventListener('close-chat', this.chatClosedHandler);

// Outside click to close tray when nothing selected (mobile only)
if (this.isTouch) {
  this.outsideClickHandler = (e) => {
    if (!this.wrapperRef) return;
    if (this.wrapperRef.contains(e.target)) return;
    const clicked = !!this.context.clicked;
    if (!this.state.micOn && !clicked && this.state.showTray) {
      this.setState({ showTray: false });
    }
  };
  // Bubble phase so inside onClick runs first
  document.addEventListener('click', this.outsideClickHandler, false);
}
}

componentWillUnmount() {
window.removeEventListener('audio-state', this.audioStateHandler);
window.removeEventListener('chat-closed', this.chatClosedHandler);
window.removeEventListener('close-chat', this.chatClosedHandler);
if (this.isTouch) {
document.removeEventListener('click', this.outsideClickHandler, false);
}
}

componentDidUpdate() {
const clicked = !!this.context.clicked;
const { micOn, showTray, hovering } = this.state;

const noneSelected = !micOn && !clicked;
const keepOpen     = micOn || clicked;

// close chat if both off
if (noneSelected && !this.lastNoneSelectedClosed) {
  window.dispatchEvent(new Event('close-chat'));
  this.lastNoneSelectedClosed = true;
} else if (!noneSelected) {
  this.lastNoneSelectedClosed = false;
}

// auto show/hide tray only on desktop and when not hovering
if (!this.isTouch && !hovering) {
  if (noneSelected && showTray) {
    // eslint-disable-next-line react/no-did-update-set-state
    this.setState({ showTray: false });
  } else if (keepOpen && !showTray) {
    // eslint-disable-next-line react/no-did-update-set-state
    this.setState({ showTray: true });
  }
}
}

// desktop hover
onWrapperEnter = () => !this.isTouch && this.setState({ hovering: true,  showTray: true  });
onWrapperLeave = () => {
if (this.isTouch) return;
const clicked = !!this.context.clicked;
const keep = this.state.micOn || clicked;
this.setState({ hovering: false, showTray: keep });
};

// mobile tap toggles tray visibility
onSupportClick = (e) => {
if (!this.isTouch || this.state.busy) return;
e.stopPropagation();
const clicked = !!this.context.clicked;
const anySelected = this.state.micOn || clicked;
this.setState((s) => ({
showTray: s.showTray ? (anySelected ? true : false) : true,
}));
};

// mic segment
toggleAudioConversation = async (e) => {
if (e) e.stopPropagation();
if (this.state.busy) return;
const { router, user, clientId, dispatch, chatbotState } = this.props;
const audio = AudioConversation.getInstance();
const isOn = audio.isConversationStarted();

try {
  this.setState({ busy: true });
  if (isOn) {
    //await audio.endConversation();
    // Immediate stop — no "Goodbye"
    await audio.stopNow();
    this.setState({ micOn: false });
  } else {
    await audio.startConversation(
      router, user, clientId, dispatch, chatbotState || { messages: [] }
    );
    this.context.setClicked(false);
    this.setState({ micOn: true, showTray: true });
  }
} catch (err) {
  console.error('toggleAudioConversation failed:', err);
} finally {
  this.setState({ busy: false });
}
};

// text segment
onKeyboard = async (e) => {
if (e) e.stopPropagation();
if (this.state.busy) return;
const { openChat, clicked, setClicked } = this.context;
const audio = AudioConversation.getInstance();

try {
  this.setState({ busy: true });

  if (clicked) {
    window.dispatchEvent(new Event('close-chat'));
    setClicked(false);
    return;
  }

  if (audio.isConversationStarted()) {
    await audio.endConversation();
    this.setState({ micOn: false });
  }
  if (typeof openChat === 'function') openChat();
  setClicked(true);
  this.setState({ showTray: true });
} catch (err) {
  console.error('keyboard toggle failed:', err);
} finally {
  this.setState({ busy: false });
}
};

// pages without tabs
traySx = {
position: 'fixed',
right: { xs: 10, sm: 20 },      // simple and reliable at xs
top:   { xs: 10, sm: 20, md: 20 },      // align with header line on all sizes
transform: 'none',
zIndex: 2000,                   // above AppBar
display: 'flex',
alignItems: 'center',
touchAction: 'manipulation',
};

// pages with tabs
traySxTabs = {
position: 'fixed',
right: { xs: 10, sm: 20 },      // simple and reliable at xs
top:   { xs: 12, sm: 20, md: 40 },      // align with header line on all sizes
transform: 'none',
zIndex: 2000,                   // above AppBar
display: 'flex',
alignItems: 'center',
touchAction: 'manipulation',
};

segmentWrapSx = (show) => ({
display: 'flex',
alignItems: 'center',
borderRadius: 3,
border: '1px solid rgba(255,255,255,0.8)',
backgroundColor: 'rgba(255,255,255,0.08)',
overflow: 'hidden',
height: 30,
opacity: show ? 1 : 0,
pointerEvents: show ? 'auto' : 'none',
transition: 'opacity 180ms ease',
});

// +10px container width → +5px per segment (39px)
segmentBtnSx = (selected, withLeftDivider = false) => ({
width: 39,
height: 30,
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
color: selected ? ICON_ON : ICON_OFF,
backgroundColor: selected ? SELECTED_BG : 'transparent',
boxSizing: 'border-box',
borderLeft: withLeftDivider ? '1px solid rgba(255,255,255,0.6)' : 'none',
margin: 0,
padding: 0,
transition: 'background-color 120ms ease, color 120ms ease',
'&:hover': {
backgroundColor: selected ? SELECTED_BG : 'rgba(255,255,255,0.10)',
color: selected ? ICON_ON : '#FFFFFF',
},
});

betaChipSx = {
color: '#FFFFFF',
backgroundColor: 'transparent',
height: 20,
fontSize: 11,
letterSpacing: 0.5,
transform: { xs: 'translateY(0)', sm: 'translateY(-6px)' },
display: { xs: 'none', sm: 'inline-flex' },   // hide on mobile
margin: 0,
padding: 0,
ml: '-3px',                                   // tuck close to icon
'& .MuiChip-label': { px: 0.5, py: 0, lineHeight: 1 },
};

render() {
//const { user } = this.props;
const { clicked } = this.context;
const { micOn, busy, showTray } = this.state;
const { pathname } = this.props.router.location;
const hasTabs = isTabbedPage(pathname);

console.log('pathname:', pathname, 'hasTabs:', hasTabs);   // <-- add this

//const isAdmin = !!(user?.is_admin || user?.isAdmin);
//if (!isAdmin) return null;

const micSelected   = micOn;
const kbSelected    = !micOn && clicked;
const supportActive = micOn || clicked;

return (
<Box
  ref={(el) => (this.wrapperRef = el)}
  sx={{
  ...(hasTabs ? this.traySxTabs : this.traySx)
}}
      onMouseEnter={this.onWrapperEnter}
    onMouseLeave={this.onWrapperLeave}
  >
    {/* Segmented icons */}
    <Box sx={this.segmentWrapSx(showTray)} onClick={(e) => e.stopPropagation()}>
      <ButtonBase
        disableRipple
        disabled={busy}
        onClick={this.toggleAudioConversation}
        aria-label={micSelected ? 'Turn mic off' : 'Turn mic on'}
        sx={this.segmentBtnSx(micSelected)}
      >
        {micSelected ? (
          <MicIcon sx={{ fontSize: 18 }} />
        ) : (
          <MicOutlinedIcon sx={{ fontSize: 18 }} />
        )}
      </ButtonBase>

      <ButtonBase
        disableRipple
        disabled={busy}
        onClick={this.onKeyboard}
        aria-label='Use keyboard (AI Assistant)'
        sx={this.segmentBtnSx(kbSelected, true)}
      >
        {kbSelected ? (
          <KeyboardIcon sx={{ fontSize: 18 }} />
        ) : (
          <KeyboardOutlinedIcon sx={{ fontSize: 18 }} />
        )}
      </ButtonBase>
    </Box>

    {/* Support icon (tap toggles tray on mobile) */}
    <ButtonBase
      disableRipple
      disabled={busy}
      onClick={this.onSupportClick}
      aria-label='Doctivity AI'
      sx={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: ICON_ON,
        opacity: supportActive ? 1 : 0.5,
        transition: 'opacity 160ms ease',
        margin: 0,
        padding: 0,
        ml: '10px', // exactly 10px from tray
        '&:focus-visible': { outline: 'none' },
      }}
    >
      <Icon sx={{ fontSize: 22 }}>support_agent</Icon>
    </ButtonBase>

    {/* BETA pill (hidden on mobile, very close to icon on desktop) */}
    <Chip label='BETA'   size='small' sx={this.betaChipSx} />
  </Box>
);
}
}

SupportIcon.propTypes = {
user: PropTypes.object.isRequired,
router: PropTypes.object.isRequired,
clientId: PropTypes.number.isRequired,
dispatch: PropTypes.func.isRequired,
chatbotState: PropTypes.shape({ messages: PropTypes.array }),
};

const mapStateToProps = (state) => ({
  chatbotState: state.app.chatbot,
});

export default withRouter(connect(mapStateToProps)(SupportIcon));